import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Box,
  Chip,
  ListItemIcon,
  Button,
  Typography,
} from "@mui/material";
import SelectArrow from "../styles/icons/SelectArrow";

/**
 * Extended multi‑select component that includes:
 *  • "Select All" option with tri‑state checkbox.
 *  • NEW "Confirm" button on the same row that simply closes the menu.
 *    ‑ The button **does not** toggle the "Select All" logic because the
 *      click event is stopped from bubbling up.
 */
export default function MultiSelectWithSelectAll({
  label,
  options = [],
  value,
  onChange,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  // Are all items currently selected?
  const allSelected = options.length > 0 && value.length === options.length;

  // Handle option selection/deselection
  const handleSelectChange = (e) => {
    let newValue = e.target.value; // array of item values user selected

    // If user clicked our special "Select All" item (with value="__all"):
    if (newValue.includes("__all")) {
      // If all are already selected, clear them; otherwise select them all
      newValue = allSelected ? [] : options.map((o) => o.value);
    }

    // Ensure it's always an array of strings/numbers
    if (typeof newValue === "string") {
      newValue = newValue.split(",");
    }

    onChange(newValue);
  };

  // Close menu without changing selection
  const handleConfirm = (e) => {
    e.stopPropagation(); // prevent Select from treating this as a selection click
    setMenuOpen(false);
  };

  return (
    <FormControl
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: "600px",
      }}
    >
      {label && <InputLabel id={`${label}-label`}>{label}</InputLabel>}
      <Select
        labelId={label ? `${label}-label` : undefined}
        multiple
        value={value}
        IconComponent={SelectArrow}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        sx={{
          borderRadius: 2,
          width: "100%",
          minWidth: "600px",
          height: "auto",
          minHeight: 50,
          maxHeight: 120,
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 0.5,
            padding: "14px",
            minHeight: "22px",
            maxHeight: "104px",
            overflow: "auto",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: 2,
          },
        }}
        onChange={handleSelectChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selectedArray) => {
          if (selectedArray.length === 0) {
            return <span style={{ color: "#999" }}>None selected</span>;
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                width: "100%",
                maxHeight: "88px",
                overflow: "auto",
              }}
            >
              {selectedArray.map((val) => {
                // Handle both object and primitive formats
                let actualValue, chipLabel;

                if (typeof val === "object" && val !== null) {
                  actualValue = val.value;
                  chipLabel = val.label;
                } else {
                  actualValue = val;
                  const matched = options.find((opt) => opt.value === val);
                  chipLabel = matched ? matched.label : val;
                }

                return (
                  <Chip
                    key={actualValue}
                    label={chipLabel}
                    size="small"
                    sx={{
                      height: "24px",
                      fontSize: "0.8125rem",
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        {/* "Select All" row with Confirm button */}
        <MenuItem value="__all">
          <ListItemIcon>
            <Checkbox
              checked={allSelected}
              indeterminate={value.length > 0 && value.length < options.length}
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />

          {/* Confirm button (does NOT toggle select‑all) */}
          <Button
            size="small"
            variant="contained"
            sx={{ ml: 2, textTransform: "none", borderRadius: 3, width: 100 }}
            color="companyBlue"
            onClick={handleConfirm}
          >
            <Typography color="textPrimary">Set</Typography>
          </Button>
        </MenuItem>

        {/* Normal items */}
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
